import { z } from 'zod';
/**
 * Enum representing the status of a source (e.g., a payment or checkout process).
 *
 * Possible values:
 * - UNPAID: The payment has not been made.
 * - PARTIAL: A partial payment has been made.
 * - PAID: The payment has been made in full.
 * - OVERPAID: More than the required amount has been paid.
 * - OVERDUE: The payment is overdue.
 * - REFUNDED: The payment has been refunded.
 * - PARTIALLY_REFUNDED: A portion of the payment has been refunded.
 * - CANCELLED: The transaction has been cancelled.
 */
export var SourceStatusEnum;
(function (SourceStatusEnum) {
    SourceStatusEnum["UNPAID"] = "UNPAID";
    SourceStatusEnum["PARTIAL"] = "PARTIAL";
    SourceStatusEnum["PAID"] = "PAID";
    SourceStatusEnum["OVERPAID"] = "OVERPAID";
    // Negative States
    SourceStatusEnum["OVERDUE"] = "OVERDUE";
    SourceStatusEnum["REFUNDED"] = "REFUNDED";
    SourceStatusEnum["PARTIALLY_REFUNDED"] = "PARTIALLY_REFUNDED";
    SourceStatusEnum["CANCELLED"] = "CANCELLED";
    SourceStatusEnum["FAILED_FINAL"] = "FAILED_FINAL";
    SourceStatusEnum["SYSTEM_REVIEW"] = "SYSTEM_REVIEW";
})(SourceStatusEnum || (SourceStatusEnum = {}));
/**
 * Enum representing the status of a source (e.g., a payment or checkout process).
 *
 * Possible values:
 * - UNPAID: The payment has not been made.
 * - PARTIAL: A partial payment has been made.
 * - PAID: The payment has been made in full.
 * - OVERPAID: More than the required amount has been paid.
 * - OVERDUE: The payment is overdue.
 * - REFUNDED: The payment has been refunded.
 * - PARTIALLY_REFUNDED: A portion of the payment has been refunded.
 * - CANCELLED: The transaction has been cancelled.
 */
export const SourceStatusSchema = z.nativeEnum(SourceStatusEnum);
/***********************************/
/**
 * Enum representing the status of an action.
 *
 * Possible values:
 * - SUCCESS: The action was successful.
 * - PENDING: The action is pending.
 * - FAILED: The action failed.
 * - REFUNDED: The action was refunded.
 * - PARTIALLY_REFUNDED: The action was partially refunded.
 */
export var ActionStatusEnum;
(function (ActionStatusEnum) {
    ActionStatusEnum["SUCCESS"] = "SUCCESS";
    ActionStatusEnum["PENDING"] = "PENDING";
    ActionStatusEnum["FAILED"] = "FAILED";
    ActionStatusEnum["REFUNDED"] = "REFUNDED";
    ActionStatusEnum["PARTIALLY_REFUNDED"] = "PARTIALLY_REFUNDED";
    ActionStatusEnum["CREATED"] = "CREATED";
    ActionStatusEnum["PAYMENT_CANCELLED"] = "PAYMENT_CANCELLED";
    ActionStatusEnum["SYSTEM_REVIEW"] = "SYSTEM_REVIEW";
})(ActionStatusEnum || (ActionStatusEnum = {}));
/**
 * Enum schema representing the status of an action.
 * Possible values:
 * - SUCCESS: The action was successful.
 * - PENDING: The action is pending.
 * - FAILED: The action failed.
 * - REFUNDED: The action was refunded.
 * - PARTIALLY_REFUNDED: The action was partially refunded.
 * - CREATED: The action was created. Can be moved to other statuses later.
 */
export const ActionStatusSchema = z.nativeEnum(ActionStatusEnum);
/***********************************/
/**
 * Enum representing the possible states of a terminal.
 *
 * Possible values:
 * - OFFLINE: The terminal is offline.
 * - READY: The terminal is ready for use.
 * - PROCESSING: The terminal is processing a request.
 * - ERROR: The terminal has encountered an error.
 */
export var TerminalStateEnum;
(function (TerminalStateEnum) {
    TerminalStateEnum["OFFLINE"] = "OFFLINE";
    TerminalStateEnum["READY"] = "READY";
    TerminalStateEnum["PROCESSING"] = "PROCESSING";
    TerminalStateEnum["ERROR"] = "ERROR";
})(TerminalStateEnum || (TerminalStateEnum = {}));
/**
 * Enum representing the possible states of a terminal.
 *
 * Possible values:
 * - OFFLINE: The terminal is offline.
 * - READY: The terminal is ready for use.
 * - PROCESSING: The terminal is processing a request.
 * - ERROR: The terminal has encountered an error.
 */
export const TerminalStateSchema = z.nativeEnum(TerminalStateEnum);
/***********************************/
/**
 * Enum representing the payout status.
 *
 * Possible values:
 * - UNSETTLED: The payout has not been settled yet.
 * - TRANSIT: The payout is in transit.
 * - COMPLETED: The payout has been completed.
 * - FAILED: The payout process failed.
 */
export var PayoutStatusEnum;
(function (PayoutStatusEnum) {
    PayoutStatusEnum["UNSETTLED"] = "UNSETTLED";
    PayoutStatusEnum["TRANSIT"] = "TRANSIT";
    PayoutStatusEnum["COMPLETED"] = "COMPLETED";
    PayoutStatusEnum["FAILED"] = "FAILED";
})(PayoutStatusEnum || (PayoutStatusEnum = {}));
/**
 * Enum representing the payout status.
 *
 * Possible values:
 * - UNSETTLED: The payout has not been settled yet.
 * - TRANSIT: The payout is in transit.
 * - PAID: The payout has been completed.
 * - FAILED: The payout process failed.
 */
export const PayoutStatusSchema = z.nativeEnum(PayoutStatusEnum);
/***********************************/
/**
 * Enum representing the notification status.
 *
 * Possible values:
 * - PENDING: The notification is pending.
 * - SENT: The notification has been sent.
 * - FAILED: The notification process failed.
 */
export var NotificationStatusEnum;
(function (NotificationStatusEnum) {
    NotificationStatusEnum["PENDING"] = "PENDING";
    NotificationStatusEnum["SENT"] = "SENT";
    NotificationStatusEnum["FAILED"] = "FAILED";
})(NotificationStatusEnum || (NotificationStatusEnum = {}));
/**
 * Enum representing the notification status.
 *
 * Possible values:
 * - PENDING: The notification is pending.
 * - SENT: The notification has been sent.
 * - FAILED: The notification process failed.
 */
export const NotificationStatusSchema = z.nativeEnum(NotificationStatusEnum);
/***********************************/
/**
 * Enum representing the payment method used.
 *
 * Possible values:
 * - CARD: Payment was made using a card.
 * - CASH: Payment was made using cash.
 * - BLE_CONNECTED_READER: Payment was made using a Bluetooth-connected reader.
 * - MANUAL_CARD: Payment was made by manually entering card details.
 * - EBT: Payment was made using EBT (Electronic Benefits Transfer).
 * - DEBT: Payment was made using a debt-based method.
 * - OTHER: Payment was made using another method.
 */
export var PaymentMethodEnum;
(function (PaymentMethodEnum) {
    /**Traditional */
    PaymentMethodEnum["CARD"] = "CARD";
    PaymentMethodEnum["CASH"] = "CASH";
    PaymentMethodEnum["CHECK"] = "CHECK";
    /** Additional  */
    PaymentMethodEnum["BLE_CONNECTED_READER"] = "BLE_CONNECTED_READER";
    PaymentMethodEnum["MANUAL_CARD"] = "MANUAL_CARD";
    PaymentMethodEnum["WEB_PAYMENT"] = "WEB_PAYMENT";
    PaymentMethodEnum["RECURRING_PAYMENT"] = "RECURRING_PAYMENT";
    PaymentMethodEnum["PAYMENT_LINK"] = "PAYMENT_LINK";
    /** Legacy mobile wire value; historical records may represent different mobile flows. */
    PaymentMethodEnum["MOBILE_APP_PAYMENT"] = "MOBILE_APP_PAYMENT";
    /** Manager-only built-in iPhone reader; maps to MOBILE_APP_PAYMENT on V4 APIs. */
    PaymentMethodEnum["TAP_TO_PAY"] = "TAP_TO_PAY";
    /** Alternative */
    PaymentMethodEnum["EBT"] = "EBT";
    PaymentMethodEnum["EBT_CASH"] = "EBT_CASH";
    PaymentMethodEnum["WIC"] = "WIC";
    PaymentMethodEnum["DEBT"] = "DEBT";
    PaymentMethodEnum["BNPL"] = "BNPL";
    //   STRIPE = 'STRIPE',
    PaymentMethodEnum["OTHER"] = "OTHER";
})(PaymentMethodEnum || (PaymentMethodEnum = {}));
/**
 * Enum representing the payment method used.
 *
 * Possible values:
 * - CARD: Payment was made using a card.
 * - CASH: Payment was made using cash.
 * - BLE_CONNECTED_READER: Payment was made using a Bluetooth-connected reader.
 * - MANUAL_CARD: Payment was made by manually entering card details.
 * - EBT: Payment was made using EBT (Electronic Benefits Transfer).
 * - DEBT: Payment was made using a debt-based method.
 * - OTHER: Payment was made using another method.
 */
export const PaymentMethodSchema = z.nativeEnum(PaymentMethodEnum);
/**
 * Statuses
 *  Types of data
 * 1. Parent(SourceStatus) Object like (Sales, Checkout, Invoice...)
 *   -- Unpaid, Partial, Paid, Failed, Overdue, Outstanding, Void, Refunded, Overpaid
 * 2. Transaction (ActionStatus) object (SUCCESS, FAILED, PENDING)
 * 3. unique object (ONLINE/OFFLINE/NOT_CONNECTED) transit, cancelled
 *  (Just use custom naming for each type of status for unique objects)
 * [Unique Objects => Terminal, Payout, Notification ]
 *
 *
 * Terminal => OFFLINE,  READY, PROCESSING, ERROR
 * Payout => UNSETTLED, TRANSIT, PAID, FAILED
 * Notification => PENDING, SENT, FAILED (Maybe use action status for this)
 */
/***********************************/
/**
 * Enum representing the status of a checkout process.
 *
 * Possible values:
 * - CHECKOUT_CREATED: The checkout process has been created.
 * - PAYMENT_WAITING: The payment is waiting to be processed.
 * - PAYMENT_PROCESSING: The payment is being processed.
 * - PAYMENT_FAILED: The payment process failed.
 * - PAYMENT_SUCCESS: The payment was successful.
 * - WAITING_CONFIRMATION: The checkout is waiting for confirmation.
 * - CHECKOUT_COMPLETED: The checkout process has been completed.
 */
export var OrderStateEnum;
(function (OrderStateEnum) {
    OrderStateEnum["WAITING_FOR_ACCEPTANCE"] = "WAITING_FOR_ACCEPTANCE";
    OrderStateEnum["ORDER_ACCEPTED"] = "ORDER_ACCEPTED";
    OrderStateEnum["ORDER_REJECTED"] = "ORDER_REJECTED";
    OrderStateEnum["ORDER_IS_BEING_PREPARED"] = "ORDER_IS_BEING_PREPARED";
    OrderStateEnum["ORDER_IS_READY_FOR_PICKUP"] = "ORDER_IS_READY_FOR_PICKUP";
    OrderStateEnum["ORDER_IS_PICKED_UP"] = "ORDER_IS_PICKED_UP";
    OrderStateEnum["ORDER_IS_CANCELLED"] = "ORDER_IS_CANCELLED";
})(OrderStateEnum || (OrderStateEnum = {}));
/**
 * Enum representing the status of a checkout/sale process.
 *
 * Possible values:
 * - CHECKOUT_CREATED: The checkout process has been created.
 * - PAYMENT_WAITING: The payment is waiting to be processed.
 * - PAYMENT_PROCESSING: The payment is being processed.
 * - PAYMENT_FAILED: The payment process failed.
 * - PAYMENT_SUCCESS: The payment was successful.
 * - WAITING_CONFIRMATION: The checkout is waiting for confirmation.
 * - CHECKOUT_COMPLETED: The checkout process has been completed.
 */
export const OrderStateSchema = z.nativeEnum(OrderStateEnum);
export const OrderLifecycleSchema = OrderStateSchema.array();
